'use client';

import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCart } from '@cobrastyle/storefront-core/hooks';
import { useCartDrawer } from '@/components/cart/cart-drawer-provider';
import { useStoreHref } from '@/components/store';
import { ROUTES } from '@/lib/routes';
import { buttonClassName } from '@cobrastyle/ui';

/**
 * Whether the shopper is standing in the checkout itself.
 *
 * Exact match, not a prefix. `/kassa/tack` is the receipt, where the order is already placed and
 * the cart has been let go of — locking the button there would strand someone who wants to carry
 * on shopping, and there is nothing left to desynchronise anyway.
 *
 * Compared against the store-prefixed path because routing may put the store code in front of it:
 * the live path is `/sv/kassa`, not `/kassa`.
 */
function useIsCheckout(): boolean {
  const pathname = usePathname();
  const storePath = useStoreHref();

  return pathname === storePath(ROUTES.checkout);
}

export function CartButton() {
  const t = useTranslations('nav');
  const { itemCount } = useCart();
  const { open } = useCartDrawer();
  const inCheckout = useIsCheckout();

  const label = itemCount > 0 ? `${t('cart')} · ${itemCount > 99 ? '99+' : itemCount}` : t('cart');
  const badgeCount = itemCount > 99 ? '99+' : itemCount;

  /**
   * The cart drawer is closed off during checkout, because editing the basket from inside it
   * leaves Svea behind.
   *
   * Svea's order is created once, up front, from the quote as it stood — removing a line
   * afterwards updates Magento and the storefront's own summary while the amount inside Svea's
   * iframe keeps the old total. Observed live: a 394 kr order became 279 kr on our side and stayed
   * 394 kr in Svea.
   *
   * Shutting the door is the fix rather than resynchronising, because the two totals can only be
   * reconciled by recreating the Svea order mid-payment — more moving parts in the one flow where
   * money is involved. Nothing else reaches the drawer from here: the only other caller of
   * `open()` is the product page's add-to-cart, which is not on this route.
   */
  const disabled = inCheckout;
  const title = disabled ? t('cartLockedInCheckout') : undefined;

  return (
    <>
      {/* Mobile: a plain icon beside the wishlist heart, like the design's
          header — the solid CTA treatment below is a desktop-only touch. */}
      <button
        type="button"
        onClick={open}
        disabled={disabled}
        title={title}
        className="relative flex h-10 w-10 items-center justify-center rounded-xl text-foreground transition-colors hover:bg-card-hover disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent lg:hidden"
        aria-label={disabled ? `${label} — ${t('cartLockedInCheckout')}` : label}
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
        </svg>
        {itemCount > 0 && (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-error px-1 text-[10px] font-semibold leading-none text-white">
            {badgeCount}
          </span>
        )}
      </button>

      <span className="hidden lg:inline-flex">
        <button
          type="button"
          onClick={open}
          disabled={disabled}
          title={title}
          // The redesign makes the cart the one solid button in the header — every
          // other action beside it is a plain text link. Routed through the design
          // system's variant rather than hand-rolled classes so it tracks the theme's
          // CTA colour with the rest of the app's buttons.
          className={buttonClassName({
            size: 'sm',
            // Canvas metrics for the one solid button in the header. Still routed
            // through the design system so it tracks the theme's CTA colour; only
            // the box metrics are overridden.
            className:
              'gap-2 rounded-sm px-[18px] py-2.5 text-[13px] font-semibold disabled:cursor-not-allowed disabled:opacity-40',
          })}
          aria-label={disabled ? `${t('cart')} — ${t('cartLockedInCheckout')}` : t('cart')}
        >
          <span className="whitespace-nowrap tabular-nums">{label}</span>
        </button>
      </span>
    </>
  );
}
